/**
 * connectionRuntimeService — Sprint 17 (Connection Runtime Engine).
 *
 * Creates and caches ISOLATED Firebase apps, one per connection:
 *   initializeApp(config, "runtime_<connectionId>")
 *
 * Returns { app, firestore, auth, storage, functions } and reuses cached apps
 * so we never initialize duplicate Firebase apps. Firebase SDK only — no REST.
 */
import { initializeApp, getApp, getApps, deleteApp } from "firebase/app";
import { getFirestore } from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { getStorage } from "firebase/storage";
import { getFunctions } from "firebase/functions";
import { getResolvedFirebaseConfig } from "./connectionRuntimeConfigService";

const RUNTIME_PREFIX = "runtime_";

/** connectionId -> { app, firestore, auth, storage, functions, name } */
const runtimeCache = new Map();

function runtimeAppName(connectionId) {
  return `${RUNTIME_PREFIX}${connectionId}`;
}

function buildServices(app, connectionId, region) {
  const firestore = getFirestore(app);
  let auth = null;
  let storage = null;
  let functions = null;
  try { auth = getAuth(app); } catch { /* auth optional */ }
  try { storage = getStorage(app); } catch { /* storage optional */ }
  try { functions = getFunctions(app, region || "us-central1"); } catch { /* functions optional */ }
  return { app, firestore, auth, storage, functions, name: runtimeAppName(connectionId) };
}

/**
 * Get (or lazily create + cache) an isolated runtime instance for a connection.
 * @param {string} connectionId
 * @param {object} connection - normalized connection WITH secrets (apiKey etc.)
 */
export function getRuntimeInstance(connectionId, connection) {
  if (!connectionId) throw new Error("connectionId is required to open a runtime instance");
  if (runtimeCache.has(connectionId)) return runtimeCache.get(connectionId);

  const name = runtimeAppName(connectionId);
  const config = getResolvedFirebaseConfig(connection);
  if (!config.apiKey || !config.projectId || !config.appId) {
    throw new Error("Connection is missing Firebase configuration (apiKey / projectId / appId).");
  }

  let app;
  const existing = getApps().find((a) => a.name === name);
  app = existing || initializeApp(config, name);

  const instance = buildServices(app, connectionId, connection.region);
  runtimeCache.set(connectionId, instance);
  return instance;
}

/** Whether a runtime instance is currently cached for a connection. */
export function hasRuntimeInstance(connectionId) {
  return runtimeCache.has(connectionId);
}

/** Terminate and remove a single runtime instance. */
export async function disposeRuntimeInstance(connectionId) {
  const instance = runtimeCache.get(connectionId);
  runtimeCache.delete(connectionId);
  if (!instance) {
    // still attempt to clean an orphan app with the expected name
    const name = runtimeAppName(connectionId);
    const orphan = getApps().find((a) => a.name === name);
    if (orphan) {
      try { await deleteApp(orphan); } catch { /* already gone */ }
    }
    return;
  }
  try { await deleteApp(instance.app); } catch { /* already gone */ }
}

/** Terminate every cached runtime except the one for `keepConnectionId`. */
export async function disposeAllExcept(keepConnectionId) {
  const ids = [...runtimeCache.keys()].filter((id) => id !== keepConnectionId);
  await Promise.all(ids.map((id) => disposeRuntimeInstance(id)));
}

/** Terminate all cached runtime instances. */
export async function disposeAllRuntimes() {
  const ids = [...runtimeCache.keys()];
  await Promise.all(ids.map((id) => disposeRuntimeInstance(id)));
}

/** Debug helper — list active runtime connection ids. */
export function listRuntimeInstances() {
  return [...runtimeCache.keys()];
}

export { getApp as getRuntimeAppByName };
